/**
 * OffboardPersonForm - offboard someone from the firm (R4-11), rendered in
 * the GlobalModal ('custom').
 *
 * Offboarding is the existing, reversible revoke-access: the person can no
 * longer sign in, their open matters and tasks go to someone else, they leave
 * their teams and departments, and their licence seat is freed (the Stripe
 * quantity follows). Here the admin also chooses WHO takes over the work -
 * by default it was always the longest-standing admin.
 *
 * Restoring them later brings their access back; work that was handed over
 * stays with whoever took it.
 */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../shared/TailwindComponents/Button';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import { Well } from '../../shared/brand/Field';
import apis from '../../../utils/apis';
import { revokeAccessPath, restoreAccessPath, revocationErrorMessage } from '../../Admin/people/accessRevocation';

export interface OffboardCandidate {
  uuid: string;
  name: string;
}

/** POST revoke-access, with the chosen successor when there is one. */
export const offboardPerson = (personUuid: string, reassignToUuid?: string | null) =>
  apis.post(revokeAccessPath(personUuid), reassignToUuid ? { reassign_to_uuid: reassignToUuid } : {});

export const restorePerson = (personUuid: string) => apis.post(restoreAccessPath(personUuid));

interface Props {
  personName: string;
  /** Active members who can take over - never the person being offboarded. */
  candidates: OffboardCandidate[];
  onOffboard: (reassignToUuid: string | null) => Promise<void>;
  onCancel: () => void;
}

const OffboardPersonForm: React.FC<Props> = ({ personName, candidates, onOffboard, onCancel }) => {
  const { t } = useTranslation();
  const [successor, setSuccessor] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const options = useMemo(
    () => candidates.map((c) => ({ value: c.uuid, label: c.name })),
    [candidates],
  );

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onOffboard(successor || null);
    } catch (e) {
      setError(revocationErrorMessage(e, t));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="offboard-form">
      <p className="text-sm text-fg-body">
        {t('{{name}} will no longer be able to sign in. You can restore their access later.', { name: personName })}
      </p>
      <Well>
        <ul className="list-disc space-y-1 ps-4 text-sm text-fg-body">
          <li>{t('Their matters and assigned tasks go to the person you choose below.')}</li>
          <li>{t('They are removed from their teams and departments.')}</li>
          <li>{t('Their licence seat is freed, and billing follows.')}</li>
        </ul>
      </Well>
      <Dropdown
        id="offboard-successor"
        label={t('Who takes over their work?')}
        options={options}
        value={successor}
        placeholder={t('An administrator (default)')}
        onChange={(v) => setSuccessor(Array.isArray(v) ? v[0] || '' : v)}
        fullWidth
      />
      {error && <p className="text-sm text-danger-600 dark:text-danger-400" role="alert">{error}</p>}
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" size="small" onClick={onCancel} disabled={busy}>{t('Cancel')}</Button>
        <Button variant="destructive" size="small" isLoading={busy} onClick={submit}>{t('Offboard')}</Button>
      </div>
    </div>
  );
};

export default OffboardPersonForm;
